import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface MfaDialogProps {
  open: boolean;
  userId: string;
  maskedEmail: string;
  onVerified: () => void;
  onCancel: () => void;
}

const MfaDialog = ({ open, userId, maskedEmail, onVerified, onCancel }: MfaDialogProps) => {
  const [code, setCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);

  // Send code when dialog opens
  useEffect(() => {
    if (open && userId && !codeSent) {
      sendCode();
    }
  }, [open, userId]);

  const sendCode = async () => {
    setIsSending(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("send-mfa-code", {
        body: { action: "send", userId },
      });
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);
      setCodeSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send code");
    } finally {
      setIsSending(false);
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) return;
    setIsVerifying(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("send-mfa-code", {
        body: { action: "verify", userId, code },
      });
      if (fnError) throw fnError;
      if (data?.success) {
        // Mark device as trusted
        const trustedDevices = JSON.parse(localStorage.getItem("uc_trusted_devices") || "[]");
        trustedDevices.push({ userId, fingerprint: getDeviceFingerprint(), trustedAt: Date.now() });
        localStorage.setItem("uc_trusted_devices", JSON.stringify(trustedDevices));
        onVerified();
      } else {
        setError(data?.error || "Invalid code");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = () => {
    setCode("");
    setCodeSent(false);
    setError(null);
    sendCode();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onCancel(); }}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Verify Your Identity
          </DialogTitle>
          <DialogDescription>
            {codeSent
              ? `We sent a 6-digit code to ${maskedEmail}. Enter it below.`
              : "Sending verification code..."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-6 py-4">
          {isSending ? (
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          ) : (
            <>
              <InputOTP maxLength={6} value={code} onChange={setCode}>
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>

              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}

              <div className="flex flex-col gap-2 w-full">
                <Button
                  onClick={handleVerify}
                  disabled={code.length !== 6 || isVerifying}
                  className="w-full"
                >
                  {isVerifying ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Verifying...
                    </>
                  ) : (
                    "Verify"
                  )}
                </Button>

                <Button variant="ghost" size="sm" onClick={handleResend} disabled={isSending}>
                  Resend Code
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Simple device fingerprint based on browser characteristics
function getDeviceFingerprint(): string {
  const nav = navigator;
  const raw = [
    nav.userAgent,
    nav.language,
    screen.width,
    screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
  ].join("|");
  // Simple hash
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    const chr = raw.charCodeAt(i);
    hash = ((hash << 5) - hash) + chr;
    hash |= 0;
  }
  return hash.toString(36);
}

export { getDeviceFingerprint };
export default MfaDialog;
